import React, { useState } from 'react';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { Nullable } from 'global';
import ReactTable from 'react-table';
import makeStyles from '@mui/styles/makeStyles';
import { Delete, Close, Edit } from '@mui/icons-material';
import DragIndicator from 'assets/img/drag-indicator.svg';
import { Typography, Button, Grid, IconButton, Modal, Stack, TextareaAutosize } from '@mui/material';
import Tile from '../../../../components/Tile/Tile';
import { VONote } from '../types';

interface Props {
  notes: VONote[];
  onNoteDelete: (note: VONote) => void;
  onNoteUpdate: (note: VONote, updatedNote: string) => void;
}

export const NotesTable = ({ notes, onNoteDelete, onNoteUpdate }: Props) => {
  const classes = useStyles();

  const [openNoteModal, setOpenNoteModal] = useState<boolean>(false);
  const [curNote, setCurNote] = useState<Nullable<VONote>>(null);
  const [editNote, setEditNote] = useState<boolean>(false);
  const [updatedNote, setUpdatedNote] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSizeValue, setPageSizeValue] = useState<number>(5);

  return (
    <Grid item className={classes.fixReactTable}>
      <ReactTable
        className={'-striped -highlight '}
        NoDataComponent={props => (
          <div {...props} className={classes.noDataStyles}>
            No rows found
          </div>
        )}
        data={notes}
        getTrProps={(state: any, rowInfo: any) => {
          return {
            style: { cursor: 'pointer' },
            onClick: () => {
              setCurNote(rowInfo.original);
              setOpenNoteModal(true);
            },
          };
        }}
        onPageChange={changePage => {
          setPageNumber(changePage);
        }}
        onPageSizeChange={changePageSize => {
          setPageSizeValue(changePageSize);
          setPageNumber(0);
        }}
        pageSize={pageSizeValue}
        page={pageNumber}
        columns={[
          {
            Header: 'Created On',
            accessor: 'createdOn',
            resizable: false,
            Cell: props => (
              <div>
                {props.value && `${moment(props.value).format('MM-DD-yyyy')}, ${moment(props.value).format('h:mm A')}`}
              </div>
            ),
            Filter: () => <></>,
          },
          {
            Header: 'Created By',
            accessor: 'createdBy',
            Cell: props => {
              return <div>{props.value && props.value}</div>;
            },
            resizable: false,
          },
          {
            Header: 'Note',
            accessor: 'note',
            resizable: true,
            Cell: props => <div>{!isEmpty(props.value) ? props.value : '--'}</div>,
            Filter: () => <></>,
          },
        ]}
      />
      <Modal
        open={openNoteModal}
        onClose={() => {
          setOpenNoteModal(false);
          setEditNote(false);
        }}
        aria-labelledby="Note View"
        aria-describedby="Input"
        disableEnforceFocus
      >
        <div style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} className={classes.surface}>
          <Tile
            title={<Typography variant="subtitle2">{curNote?.createdBy}</Typography>}
            maxHeightLimitByScreen
            titleButtonComponent={
              <Stack direction="row" gap="0.5rem">
                <IconButton
                  color="primary"
                  onClick={() => {
                    setEditNote(true);
                  }}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => {
                    if (!curNote) return;
                    onNoteDelete(curNote);
                    setOpenNoteModal(false);
                  }}
                >
                  <Delete />
                </IconButton>
                <IconButton
                  sx={{ color: 'black' }}
                  onClick={() => {
                    setOpenNoteModal(false);
                    setEditNote(false);
                  }}
                >
                  <Close />
                </IconButton>
              </Stack>
            }
          >
            {editNote ? (
              <>
                <TextareaAutosize
                  onChange={e => setUpdatedNote(e.target.value)}
                  className={classes.notesBox}
                  defaultValue={curNote?.note}
                />
                <Button
                  onClick={() => {
                    if (!curNote) return;
                    onNoteUpdate(curNote, updatedNote);
                    setOpenNoteModal(false);
                  }}
                >
                  SAVE
                </Button>
              </>
            ) : (
              <>
                <Typography style={{ marginBottom: 24 }}>{curNote?.note}</Typography>
                <Typography>
                  {moment(curNote?.createdOn).format('MM-DD-yyyy')}, {moment(curNote?.createdOn).format('h:mm A')}
                </Typography>
              </>
            )}
          </Tile>
        </div>
      </Modal>
    </Grid>
  );
};

const useStyles = makeStyles({
  fixReactTable: {
    '& .ReactTable .pagination-bottom button': {
      height: '40px',
    },
    '& .ReactTable .-pagination .-center': {
      flexWrap: 'nowrap',
    },
    '& .ReactTable .-pageInfo': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .ReactTable .-totalPages': {
      marginLeft: '10px',
    },
    '& .ReactTable .rt-thead .rt-resizable-header-content': {
      position: 'relative',
      paddingRight: '1rem',
      overflow: 'visible',
      display: 'flex',
      alignItems: 'center',
    },
    '& .ReactTable .rt-resizer': {
      backgroundImage: `url(${DragIndicator})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'left center',
      backgroundSize: '26px',
    },
  },
  noDataStyles: {
    display: 'block',
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'rgba(255, 255, 255, 0.8)',
    transition: 'all 0.3s ease',
    zIndex: '1',
    pointerEvents: 'none',
    padding: '20px',
    color: 'rgba(0, 0, 0, 0.5)',
  },
  surface: {
    position: 'absolute',
    width: '50%',
    display: 'flex',
    '@media (max-width: 660px)': {
      width: '90%',
      minWidth: '0',
    },
  },
  notesBox: {
    overflowY: 'auto',
    marginTop: '10px',
    resize: 'none',
    width: '100%',
    minHeight: '100px',
    maxHeight: '200px',
    border: '1px solid #DDD',
    borderRadius: '10px',
    padding: '5px',
  },
});
